<template>
  <v-card>
    <v-card-title>
      {{ card_title }}
      <v-spacer></v-spacer>
      <slot name="actions"></slot>
    </v-card-title>

    <v-card-subtitle v-if="card_subtitle" class="pb-0">
      {{ card_subtitle }}
    </v-card-subtitle>

    <v-card-text>
      <v-row class="mb-4" align="center">
        <!-- Search field -->
        <v-col cols="12" :md="$scopedSlots.filters ? 3 : 6">
          <v-text-field
            v-model="full_text"
            prepend-icon="mdi-magnify"
            :label="search_label"
            single-line
            hide-details
            @input="on_input"
            autocomplete="off"
            clearable
            :placeholder="search_placeholder"
          />
        </v-col>
        <!-- State filter (built-in) -->
        <v-col cols="12" :md="$scopedSlots.filters ? 3 : 6">
          <v-select
            v-model="selected_state_filter"
            :items="state_filter_options"
            label="Filter by Status"
            item-text="text"
            item-value="value"
            outlined
            dense
            hide-details
            clearable
            prepend-inner-icon="mdi-filter-variant"
            @change="on_state_filter_change"
          />
        </v-col>
        <!-- Custom filters slot -->
        <v-col v-if="$scopedSlots.filters" cols="12" md="6">
          <slot name="filters"></slot>
        </v-col>
      </v-row>

      <slot
        :results="results"
        :loading="loading"
        :footer_props="footer_props"
        :computed_no_data_text="computed_no_data_text"
      />
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'ImportsSearchCard',
  props: {
    card_title: { type: String, required: true },
    card_subtitle: { type: String, default: null },
    endpoint_base: { type: String, required: true },
    search_label: { type: String, default: 'Search imports...' },
    search_placeholder: { type: String, default: 'Search by file name, imported by, status, date, or record count...' },
    loading_text: { type: String, default: 'Loading imports...' },
    no_data_base_text: { type: String, default: 'No imports available' },
    max_results: { type: Number, default: 1000 },
    auto_load: { type: Boolean, default: true },
    buildParams: { type: Function, default: null },
    transformResults: { type: Function, default: null },
    refresh_key: { type: [String, Number], default: 0 },
    additional_params: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      full_text: '',
      selected_state_filter: null,
      results: null,
      loading: false,
      debounce_timer: null
    };
  },
  computed: {
    footer_props() {
      return {
        itemsPerPageText: 'Imports per page:',
        itemsPerPageOptions: [25, 50, 100],
        disableItemsPerPage: false,
        disablePagination: false
      };
    },
    computed_no_data_text() {
      if (this.full_text) {
        return `No items found matching "${this.full_text}"`;
      }
      return this.no_data_base_text;
    },
    state_filter_options() {
      const options = [];
      const enum_choices = this.$rc.enumChoices('import_state');
      
      // Add options from enum, skip placeholder values
      if (enum_choices && enum_choices.length > 0) {
        for (let i = 0; i < enum_choices.length; i++) {
          const choice = enum_choices[i];
          // Skip empty/placeholder values and "Any/None" type options
          if (choice.value && choice.value !== '' && 
              !choice.text.toLowerCase().includes('any') && 
              !choice.text.toLowerCase().includes('none')) {
            options.push({
              value: choice.value,
              text: choice.text
            });
          }
        }
      }
      
      return options;
    }
  },
  mounted() {
    if (this.auto_load) {
      this.load();
    }
  },
  watch: {
    refresh_key() {
      this.load();
    },
    additional_params: {
      handler() {
        this.load();
      },
      deep: true
    }
  },
  beforeDestroy() {
    if (this.debounce_timer) {
      clearTimeout(this.debounce_timer);
    }
  },
  methods: {
    on_input() {
      if (this.debounce_timer) {
        clearTimeout(this.debounce_timer);
      }
      this.debounce_timer = setTimeout(() => {
        this.load();
      }, 751);
    },

    on_state_filter_change() {
      this.load();
    },

    make_params(full_text) {
      const base_params = this.buildParams
        ? this.buildParams(full_text, this.max_results)
        : {
            start: 0,
            count: this.max_results,
            full_text: full_text
          };
      
      // Add state filter if selected
      if (this.selected_state_filter) {
        base_params.state = this.selected_state_filter;
      }
      
      // Merge additional_params into base params
      return { ...base_params, ...this.additional_params };
    },

    load() {
      this.loading = true;

      const organization_id = this.$route.params.organization_id;
      const params = this.make_params(this.full_text);

      this.$rc.rest({
        spinner: false,
        endpoint: 'LIST: ' + this.endpoint_base + '/search/' + organization_id,
        params,
        on_success: (data) => {
          this.loading = false;
          const raw_results = (data && data.results) ? data.results : [];
          this.results = this.transformResults ? this.transformResults(raw_results) : raw_results;
          this.$emit('loaded', this.results);
        },
        on_error: (error) => {
          this.loading = false;
          this.$rc.alert(
            'Error Loading Imports',
            'Unable to load import history: ' + error.message
          );
        }
      });
    },

    refresh() {
      this.load();
    }
  }
};
</script>

<style scoped>
/* Table styling is inherited from parent pages */
</style>

